import { useMutation, useReactiveVar } from '@apollo/client/react'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { UpdateNuggetElementInput } from '@deltek/specification-client/core/Models/nugget'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { TextField } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { SetDrawingDesignation } from 'api/graphQL/mutations/projectElement.mutations'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { workspaceContents } from 'api/graphQL/reactiveVars/workspaceContent'
import React from 'react'
import { useParams } from 'react-router-dom'
import { WorkspaceActionBarContext } from '../WorkspaceActionBarContext'

const useShowHideBarStyles = makeStyles((theme: Theme) => ({
    label: {
        fontSize: pxToRem(14),
        color: '#304959',
        fontWeight: 400,
        textTransform: 'uppercase',
        paddingLeft: theme.spacing(0.9),
        paddingRight: 0,
        paddingBottom: theme.spacing(0.8),
    },
    textField: {
        width: '100%',
        paddingLeft: theme.spacing(1),
    },
    textlabel: {
        paddingLeft: theme.spacing(1),
    },
    drawingTagContainer: {
        width: '100%',
    },
}))

export interface DrawingTagFieldProps {
    tag: string
    elementId: string
}

const DrawingTagField: React.FC<DrawingTagFieldProps> = ({
    tag,
    elementId,
}) => {
    const classes = useShowHideBarStyles()
    const { currentState } = React.useContext(
        WorkspaceActionBarContext
    )
    const [drawingTag, setDrawingTag] = React.useState<string>('')
    const defaultValue = 'Insert drawing designation'

    const { projectId } = useParams<{ projectId?: string }>()
    const { projectProductFamilyId: productFamilyId } = useReactiveVar(
        workspaceContents
    )
    const { isReviewer } = useReactiveVar(userInfo)
    const isDisabled = React.useMemo(() => isReviewer, [isReviewer])

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [setDrawingDesignation] = useMutation<
        { setDrawingDesignation: boolean },
        { input: Partial<UpdateNuggetElementInput> }
    >(SetDrawingDesignation, {
        onCompleted: (data) => {
            if (data.setDrawingDesignation) {
                showGlobalSnackbar?.({
                    variables: {
                        message: 'Successfully setting the drawing tag',
                        variant: 'success',
                        id: `DrawingTag_SettingSuccess`,
                    },
                })
                currentState["retrieveUpdatedNugget"]?.(false, true, true)
            }
        },
        onError: (error) => {
            showGlobalSnackbar?.({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: `DrawingTag_SettingError`,
                },
            })
        },
    })

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDrawingTag(event.currentTarget.value)
    }
    const autoSaveTag = () => {
        if (tag !== drawingTag) {
            if (drawingTag !== '') {
                setDrawingDesignation({
                    variables: {
                        input: {
                            elementId,
                            projectId,
                            drawingDesignation: drawingTag,
                            productFamilyId: productFamilyId,
                        },
                    },
                })
            } else {
                if (tag !== defaultValue) {
                    setDrawingTag(defaultValue)
                    setDrawingDesignation({
                        variables: {
                            input: {
                                elementId,
                                projectId,
                                drawingDesignation: defaultValue,
                                productFamilyId: productFamilyId,
                            },
                        },
                    })
                }
            }
        }
    }

    React.useEffect(() => {
        setDrawingTag(tag)
    }, [tag])

    return (
        <GridContainer
            direction="column"
            data-testid="drawingTagContainer"
            className={classes.drawingTagContainer}
        >
            <GridItem>
                <TextField
                    label={defaultValue}
                    className={classes.textField}
                    variant="outlined"
                    size="small"
                    value={drawingTag === defaultValue ? '' : drawingTag}
                    onChange={handleChange}
                    onBlur={autoSaveTag}
                    data-testid="drawingTag"
                    InputLabelProps={{
                        classes: {
                            outlined: classes.textlabel,
                        },
                    }}
                    inputProps={{ maxLength: 50 }}
                    disabled={isDisabled}
                />
            </GridItem>
        </GridContainer>
    )
}
export default DrawingTagField
